/**
 * Core element renderer and small helpers (highlight-agnostic)
 */

import { createElement, ReactNode, ReactElement } from 'react';
import { AstElement, AstText, BlockAnnotationsLookup } from '../types';
import { HEADING_ELEMENTS, TABLE_ELEMENTS, VOID_ELEMENTS } from '../constants';
import mergeHighlights from '../highlighter/mergeAfterRendering';
import { createHeading } from './heading';

/**
 * Filters out whitespace-only text nodes in table structures. The original HTML
 * string often has whitespace inside table structures that is not valid HTML
 * and leads to warnings, so we need to sanitize tables.
 */
function sanitizeTableWhitespace(
  children: ReactNode[],
  parentTagName: string,
): ReactNode[] {
  if (!TABLE_ELEMENTS.has(parentTagName)) {
    /* The parent element is not a table, we don't need to sanitize the
    whitespace in the children. */
    return children;
  }

  return children.filter((child) => {
    if (typeof child === 'string') {
      // Drop whitespace-only text nodes. Whitespace-only text nodes in tables are not valid HTML.
      return child.trim() !== '';
    }
    return true;
  });
}
/**
 * Renders an AST element node
 */
export default function renderElement(
  node: AstElement,
  renderText?: (textNode: AstText) => ReactNode,
  annotationsByBlock?: BlockAnnotationsLookup,
): ReactElement {
  const { tag: tagName, attrs: attributes = {}, children, blockId } = node;
  const isHeading = HEADING_ELEMENTS.has(tagName);
  const isVoid = VOID_ELEMENTS.has(tagName);

  if (isVoid) {
    return createElement(tagName, {
      ...attributes,
      key: `void-${node.keyPath.join('-')}`,
    });
  }

  const renderedChildren: ReactNode[] = children.map((child) => {
    if (child.type === 0) {
      if (renderText) {
        return renderText(child);
      }
      return child.text;
    } else {
      return renderElement(child, renderText, annotationsByBlock);
    }
  });

  const flattenedRenderedChildren: ReactNode[] = renderedChildren.flat();

  const sanitizedChildren = sanitizeTableWhitespace(
    flattenedRenderedChildren,
    tagName,
  );

  let finalChildren: ReactNode[] = sanitizedChildren;

  // Merge highlights across the children of the element if there are any
  if (blockId && annotationsByBlock) {
    finalChildren = mergeHighlights(
      sanitizedChildren,
      blockId,
      annotationsByBlock,
    );
  }

  if (isHeading) {
    return createHeading(tagName, attributes, finalChildren, blockId as number); // We know that headings always have a blockId
  }

  return createElement(
    tagName,
    {
      ...attributes,
      ...(blockId
        ? {
            'data-block-id': blockId,
            id: (attributes as any).id ?? `b${blockId}`, // eslint-disable-line @typescript-eslint/no-explicit-any
          }
        : {}),
      key: `element-${node.keyPath.join('-')}`,
    },
    finalChildren,
  );
}
